/*
 * Pricing and buying a made-to-measure product.
 *
 * The shop never invents a price. Every amount shown here comes from a coordinate that exists in the
 * approved grid the product carries, rounded up to the next step because that is the size that will
 * actually be manufactured. When the customer's answers do not land on a coordinate, the page says
 * why and refuses to sell, rather than guessing.
 */
(function () {
  'use strict';

  var OUTCOMES = {
    ready: 'ready',
    incomplete: 'incomplete',
    underMinimum: 'under-minimum',
    overMaximum: 'over-maximum',
    customColour: 'custom-colour',
    uncaptured: 'uncaptured'
  };

  function readJson(selector) {
    var node = document.querySelector(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  function roundUpToStep(value, step) {
    if (!step || step <= 0) return value;
    return Math.ceil(value / step) * step;
  }

  function Configurator(form) {
    this.form = form;
    this.lookup = readJson('[data-pricing-lookup]');
    this.tiers = readJson('[data-price-tier-variants]') || {};
    this.bounds = readJson('[data-product-bounds]') || {};
    this.strings = readJson('[data-product-translations]') || {};

    this.variantInput = form.querySelector('[data-variant-id]');
    this.submit = form.querySelector('[data-add-to-cart]');
    this.priceNode = document.querySelector('[data-resolved-price]');
    this.alertNode = form.querySelector('[data-configurator-alert]');
    this.customPanel = document.querySelector('[data-colour-custom-panel]');
  }

  Configurator.prototype.readAnswers = function () {
    var lookup = this.lookup || {};
    var widthField = this.form.querySelector('[data-source-name="' + lookup.widthSourceName + '"]');
    var heightField = this.form.querySelector('[data-source-name="' + lookup.heightSourceName + '"]');
    var colour = this.form.querySelector('[data-colour-standard]:checked');
    var custom = this.form.querySelector('[data-colour-custom]:checked');
    var selection = this.form.querySelector('[data-selection-source-id]:checked');

    return {
      width: widthField && widthField.value !== '' ? Number(widthField.value) : null,
      height: heightField && heightField.value !== '' ? Number(heightField.value) : null,
      profile: colour ? colour.value : null,
      customColour: Boolean(custom),
      selection: selection ? selection.getAttribute('data-selection-source-id') : null
    };
  };

  Configurator.prototype.resolve = function (answers) {
    var lookup = this.lookup;
    if (!lookup) return { outcome: OUTCOMES.uncaptured };

    if (answers.customColour) return { outcome: OUTCOMES.customColour };
    if (answers.width === null || answers.height === null || !answers.profile) {
      return { outcome: OUTCOMES.incomplete };
    }

    var bounds = this.bounds;
    if (bounds.widthMin && answers.width < bounds.widthMin) return { outcome: OUTCOMES.underMinimum };
    if (bounds.heightMin && answers.height < bounds.heightMin) return { outcome: OUTCOMES.underMinimum };
    if (bounds.widthMax && answers.width > bounds.widthMax) return { outcome: OUTCOMES.overMaximum };
    if (bounds.heightMax && answers.height > bounds.heightMax) return { outcome: OUTCOMES.overMaximum };

    var step = lookup.gridStepCm;
    var width = roundUpToStep(answers.width, step);
    var height = roundUpToStep(answers.height, step);

    var profileIndex = (lookup.profiles || []).indexOf(answers.profile);
    if (profileIndex < 0) return { outcome: OUTCOMES.uncaptured };

    var selectionIndex = 0;
    var selections = lookup.selections || [];
    if (selections.length > 1) {
      if (!answers.selection) return { outcome: OUTCOMES.incomplete };
      selectionIndex = selections.findIndex(function (entry) {
        return entry.split('=')[1] === answers.selection;
      });
      if (selectionIndex < 0) return { outcome: OUTCOMES.uncaptured };
    }

    var match = (lookup.coordinates || []).find(function (coordinate) {
      return coordinate[0] === width
        && coordinate[1] === height
        && coordinate[2] === profileIndex
        && coordinate[3] === selectionIndex;
    });
    if (!match) return { outcome: OUTCOMES.uncaptured };

    var tierLabel = (lookup.tiers || [])[match[4]];
    var variant = this.tiers[tierLabel];
    if (!variant || !variant.available) return { outcome: OUTCOMES.uncaptured };

    return {
      outcome: OUTCOMES.ready,
      variant: variant,
      billedWidth: width,
      billedHeight: height
    };
  };

  Configurator.prototype.renderAlert = function (outcome) {
    if (!this.alertNode) return;
    var messages = {};
    messages[OUTCOMES.incomplete] = { kind: 'info', text: this.strings.incomplete };
    messages[OUTCOMES.underMinimum] = { kind: 'warning', text: this.strings.underMinimum };
    messages[OUTCOMES.overMaximum] = { kind: 'warning', text: this.strings.overMaximum };
    messages[OUTCOMES.uncaptured] = { kind: 'warning', text: this.strings.uncaptured };
    messages[OUTCOMES.customColour] = { kind: 'info', text: this.strings.customColour };

    var message = messages[outcome];
    if (!message || !message.text) {
      this.alertNode.innerHTML = '';
      return;
    }

    var role = message.kind === 'error' ? 'alert' : 'status';
    this.alertNode.innerHTML =
      '<div class="hvo-alert hvo-alert--' + message.kind + '" role="' + role + '">' +
      '<span class="hvo-alert__body"><span class="hvo-alert__text"></span></span></div>';
    this.alertNode.querySelector('.hvo-alert__text').textContent = message.text;
  };

  Configurator.prototype.update = function () {
    var answers = this.readAnswers();
    var result = this.resolve(answers);

    if (this.customPanel) {
      this.customPanel.hidden = !answers.customColour;
    }

    var ready = result.outcome === OUTCOMES.ready;

    if (this.priceNode) {
      this.priceNode.hidden = !ready;
      if (ready) {
        var valueNode = this.priceNode.querySelector('.hvo-stated__value');
        if (valueNode) valueNode.textContent = result.variant.priceFormatted;
      }
    }

    if (this.variantInput && ready) {
      this.variantInput.value = String(result.variant.id);
    }

    if (this.submit) {
      this.submit.disabled = !ready;
    }

    this.renderAlert(result.outcome);
  };

  Configurator.prototype.addToCart = function (event) {
    event.preventDefault();
    if (!this.submit || this.submit.disabled) return;

    var self = this;
    var original = this.submit.textContent;
    this.submit.disabled = true;
    if (this.strings.adding) this.submit.textContent = this.strings.adding;

    fetch(this.bounds.cartAddUrl, {
      method: 'POST',
      headers: { Accept: 'application/javascript' },
      body: new FormData(this.form)
    })
      .then(function (response) {
        if (!response.ok) throw new Error('add-to-cart-failed');
        return fetch(window.Shopify.routes.root + 'cart.js', { headers: { Accept: 'application/json' } });
      })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent('hvo:cart-updated', { detail: { itemCount: cart.item_count } }));
        window.location.assign(self.bounds.cartUrl);
      })
      .catch(function () {
        self.submit.disabled = false;
        self.submit.textContent = original;
        self.renderAlertText(self.strings.addFailed);
      });
  };

  Configurator.prototype.renderAlertText = function (text) {
    if (!this.alertNode || !text) return;
    this.alertNode.innerHTML =
      '<div class="hvo-alert hvo-alert--error" role="alert">' +
      '<span class="hvo-alert__body"><span class="hvo-alert__text"></span></span></div>';
    this.alertNode.querySelector('.hvo-alert__text').textContent = text;
  };

  Configurator.prototype.start = function () {
    var self = this;
    this.form.addEventListener('input', function () { self.update(); });
    this.form.addEventListener('change', function () { self.update(); });
    this.form.addEventListener('submit', function (event) { self.addToCart(event); });
    this.update();
  };

  function bindSteppers() {
    document.querySelectorAll('[data-stepper]').forEach(function (stepper) {
      var input = stepper.querySelector('[data-stepper-input]');
      if (!input) return;

      function nudge(delta) {
        var min = input.min === '' ? -Infinity : Number(input.min);
        var max = input.max === '' ? Infinity : Number(input.max);
        var next = Math.min(max, Math.max(min, Number(input.value || min) + delta));
        input.value = String(next);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      var down = stepper.querySelector('[data-stepper-down]');
      var up = stepper.querySelector('[data-stepper-up]');
      if (down) down.addEventListener('click', function () { nudge(-1); });
      if (up) up.addEventListener('click', function () { nudge(1); });
    });
  }

  function bindGallery() {
    var gallery = document.querySelector('[data-gallery]');
    if (!gallery) return;

    gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var index = thumb.getAttribute('data-gallery-thumb');
        gallery.querySelectorAll('[data-gallery-frame]').forEach(function (frame) {
          frame.hidden = frame.getAttribute('data-gallery-frame') !== index;
        });
        gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (other) {
          other.setAttribute('aria-current', other === thumb ? 'true' : 'false');
        });
      });
    });
  }

  function start() {
    bindSteppers();
    bindGallery();
    var form = document.querySelector('[data-configurator]');
    if (form) new Configurator(form).start();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
